import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { BellRing, Mail, Plug, Receipt, Ruler, Settings2, Workflow, type LucideIcon } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { PageHeader } from '@/components/page-header';
import {
  Badge,
  Button,
  Card,
  CardContent,
  ErrorState,
  Skeleton,
  Switch,
  useToast,
} from '@/components/ui';
import { ApiError } from '@/lib/api';
import { useAuth } from '@/lib/auth';
import { OutboundQueue } from './outbound-queue';
import { ProcessSettingsDialog } from './process-settings-dialog';
import { PROCESSES_KEY, processesApi, type Process } from './processes-api';

const ICONS: Record<Process['icon'], LucideIcon> = {
  workflow: Workflow,
  bell: BellRing,
  ruler: Ruler,
  receipt: Receipt,
};

/**
 * מרקטפלייס התהליכים. כל כרטיס הוא אגנט נפרד בשרת (src/processes/<key>);
 * המתג כאן הוא כל מה שמפעיל או מכבה אותו.
 */
export function ProcessesPage() {
  const { t } = useTranslation(['processes', 'common']);
  const toast = useToast();
  const qc = useQueryClient();
  const { can } = useAuth();
  const isOwner = can('OWNER');
  const [editing, setEditing] = useState<Process | null>(null);

  const list = useQuery({
    queryKey: PROCESSES_KEY,
    queryFn: ({ signal }) => processesApi.list(signal),
  });

  const toggle = useMutation({
    mutationFn: ({ key, enabled }: { key: string; enabled: boolean }) =>
      processesApi.update(key, { enabled }),
    onSuccess: (p) => {
      toast.success(p.enabled ? t('page.enabled', { name: p.name }) : t('page.disabled', { name: p.name }));
      void qc.invalidateQueries({ queryKey: PROCESSES_KEY });
    },
    onError: (err: unknown) =>
      toast.error(t('page.toggleFailed'), err instanceof ApiError ? err.message : undefined),
  });

  return (
    <div className="mx-auto max-w-5xl space-y-6">
      <PageHeader
        title={t('page.title')}
        description={t('page.description')}
      />

      {list.isPending ? (
        <div className="grid gap-4 md:grid-cols-2">
          {[0, 1, 2].map((i) => (
            <Skeleton key={i} className="h-64" />
          ))}
        </div>
      ) : list.isError ? (
        <ErrorState error={list.error} onRetry={() => void list.refetch()} />
      ) : (
        <ul className="grid gap-4 md:grid-cols-2">
          {list.data.filter((p) => p.relevant).map((p) => {
            const Icon = ICONS[p.icon];
            const available = p.status === 'available';
            return (
              <li key={p.key}>
                <Card className="flex h-full flex-col">
                  <CardContent className="flex flex-1 flex-col gap-4">
                    <div className="flex items-start justify-between gap-3">
                      <div className="flex items-start gap-3">
                        <span className="grid size-10 shrink-0 place-items-center rounded-(--radius-md) bg-accent-subtle text-accent">
                          <Icon className="size-5" aria-hidden />
                        </span>
                        <div>
                          <h2 className="text-sm font-semibold text-fg">{p.name}</h2>
                          <p className="mt-0.5 text-xs text-fg-muted">{p.tagline}</p>
                        </div>
                      </div>
                      {available ? (
                        <Switch
                          checked={p.enabled}
                          disabled={!isOwner || toggle.isPending}
                          onCheckedChange={(enabled) => toggle.mutate({ key: p.key, enabled })}
                          aria-label={t('page.toggleAria', { name: p.name })}
                        />
                      ) : (
                        <Badge tone="neutral" className="shrink-0 whitespace-nowrap">
                          {t('page.comingSoon')}
                        </Badge>
                      )}
                    </div>

                    <ol className="space-y-2">
                      {p.steps.map((step, i) => (
                        <li key={step} className="flex gap-2.5 text-sm text-fg">
                          <span className="grid size-5 shrink-0 place-items-center rounded-full bg-surface-sunken text-2xs font-semibold text-fg-muted">
                            {i + 1}
                          </span>
                          <span>{step}</span>
                        </li>
                      ))}
                    </ol>

                    <div className="mt-auto flex flex-wrap items-center justify-between gap-2 border-t border-border pt-3">
                      <span className="flex items-center gap-1.5 text-2xs text-fg-subtle">
                        {p.requires.includes('greenInvoice') ? (
                          <>
                            <Plug className="size-3.5" aria-hidden />
                            {t('page.requiresGreenInvoice')}
                          </>
                        ) : p.key === 'carpentry-drawing-quote' ? (
                          <>
                            <Ruler className="size-3.5" aria-hidden />
                            {t('page.drawingHint')}
                          </>
                        ) : p.recommends.includes('gmail') ? (
                          <>
                            <Mail className="size-3.5" aria-hidden />
                            {t('page.gmailHint')}
                          </>
                        ) : null}
                      </span>
                      {available && isOwner ? (
                        <Button variant="ghost" size="sm" onClick={() => setEditing(p)}>
                          <Settings2 aria-hidden />
                          {t('page.settings')}
                        </Button>
                      ) : null}
                    </div>
                  </CardContent>
                </Card>
              </li>
            );
          })}
        </ul>
      )}

      <OutboundQueue />

      {editing ? (
        <ProcessSettingsDialog process={editing} onClose={() => setEditing(null)} />
      ) : null}
    </div>
  );
}
